import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users } from 'lucide-react';
import { AppShell } from '@/acc-ui/components/AppShell';
import { PageHeader, Card, EmptyState } from '@/acc-ui/primitives/Layout';
import { DataTable, type Column } from '@/acc-ui/primitives/DataTable';
import { Badge } from '@/acc-ui/primitives/Badge';
import { SearchInput } from '@/acc-ui/primitives/SearchInput';
import { HighlightText } from '@/acc-ui/primitives/HighlightText';
import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { accessApi, type UserListItem } from '@/api/access-api';

/**
 * Team — a simplified, read-only view of users.
 *
 * Designed for users with ACCESS_VIEW but not ACCESS_MANAGE_USERS. It's
 * the "who has access to what" screen. Power users manage from the Access
 * Manager page instead.
 */

export function TeamPage() {
  const [page, setPage] = useState(1);
  const pageSize = 25;
  const [search, setSearch] = useState('');

  const debouncedSearch = useDebouncedValue(search.trim(), 300);
  const usersQuery = useQuery({
    queryKey: ['team', { page, pageSize, search: debouncedSearch }],
    queryFn: () => accessApi.listUsers({ page, pageSize, search: debouncedSearch || undefined }),
    placeholderData: (prev) => prev,
  });

  const columns: Column<UserListItem>[] = useMemo(
    () => [
      {
        key: 'user',
        header: 'Member',
        cell: (u) => (
          <div className="flex flex-col">
            <span className="font-medium text-text-primary">
              <HighlightText text={u.displayName} query={debouncedSearch} />
            </span>
            <span className="text-caption text-text-tertiary">
              <HighlightText text={u.email} query={debouncedSearch} />
            </span>
          </div>
        ),
      },
      {
        key: 'roles',
        header: 'Roles',
        cell: (u) => (
          <div className="flex flex-wrap gap-1">
            {u.isSuperAdmin && <Badge tone="info" dot>Super Admin</Badge>}
            {u.roles.length === 0 && !u.isSuperAdmin ? (
              <span className="text-text-muted text-caption italic">No roles</span>
            ) : (
              u.roles.map((r) => <Badge key={r} tone="muted">{r}</Badge>)
            )}
          </div>
        ),
      },
      {
        key: 'status',
        header: 'Status',
        cell: (u) =>
          u.isActive ? (
            <Badge tone="success" dot>Active</Badge>
          ) : (
            <Badge tone="neutral" dot>Inactive</Badge>
          ),
        width: '8rem',
      },
    ],
    [debouncedSearch],
  );

  const showEmpty =
    !usersQuery.isLoading && usersQuery.data && usersQuery.data.total === 0 && !search;

  return (
    <AppShell crumb="Admin" title="Team">
      <PageHeader
        title="Team"
        subtitle="Everyone with access to Agentic Loan Audit at this deployment."
      />

      {showEmpty ? (
        <Card>
          <EmptyState
            icon={<Users className="w-5 h-5" />}
            title="No teammates yet"
            description="Once your colleagues sign in with Office 365, they will appear here. An administrator then grants them roles in Access Manager."
          />
        </Card>
      ) : (
        <>
          <div className="flex items-center">
            <div className="flex-1 max-w-md">
              <SearchInput
                placeholder="Search by name or email…"
                value={search}
                onChange={(v) => {
                  setSearch(v);
                  setPage(1);
                }}
              />
            </div>
          </div>
          <DataTable<UserListItem>
            columns={columns}
            rows={usersQuery.data?.items ?? []}
            rowKey={(u) => u.id}
            page={page}
            pageSize={pageSize}
            total={usersQuery.data?.total ?? 0}
            onPageChange={setPage}
            loading={usersQuery.isLoading}
            emptyMessage={debouncedSearch ? `No members match “${debouncedSearch}”.` : 'No members to show.'}
          />
        </>
      )}
    </AppShell>
  );
}
